import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, NgModule, OnInit, Output } from "@angular/core";
import { FormControl, FormsModule, NgModelGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { AegisIntegrationService } from "src/app/_services/aegisIntegration.service";

@Component({
    selector: 'app-add-candidate-aegis-modal',
    imports: [FormsModule, ReactiveFormsModule, CommonModule],
    templateUrl: './add-candidate-aegis-modal.component.html',
    styleUrls: ['./add-candidate-aegis-modal.component.scss']
})
export class AddCandidateAegisModalComponent implements OnInit {

    @Input() candidates: any[] = [];
    @Input() calledfrom: string | null = null;

    @Output() update: EventEmitter<{ status: 'success' | 'error' | 'cancel' | 'unauthorized', message: string, data?: any }> = new EventEmitter();
    searchMandateFormControll: FormControl = new FormControl('');

    mandateList: any[] = [];
    selectedMandateId: number | null = null;
    successMessage: string = '';
    errorMessage: string = '';

    currentPage: number = 1;
    start: number = 0;
    limit: number = 10;
    mandatesPerPage: number = 10;

    showAddToAegisSpinner: boolean = false;
    isLoadingMandates: boolean = false;

    constructor(private aegisIntegrationService: AegisIntegrationService) { }

    ngOnInit() {

        this.selectedMandateId = null;
        this.currentPage = 1;

        this.searchMandateFormControll.valueChanges.pipe(
            map((value: string) => (value || '').toString().trim()),
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe((searchTerm: string) => {
            this.selectedMandateId = null;
            this.currentPage = 1;
            this.getAegisMandateList(searchTerm.length > 0 ? searchTerm : null);
        });
        this.getAegisMandateList(null);

    };


    /**
     * Flash a toast message.
     * @param message Message to display
     * @param type Type of message
     */
    private toastmessage(message: string, type: 'success' | 'error') {
        if (type == 'success') {
            this.successMessage = message;
            setTimeout(() => this.successMessage = '', 5000);
        } else if (type == 'error') {
            this.errorMessage = message;
            setTimeout(() => this.errorMessage = '', 5000);
        }
    };

    /**
     * Fetches the Aegis mandate list based on the provided search name.
     * @param searchname The name to search for in the Aegis mandate list.
     */
    getAegisMandateList(searchname: string | null): void {
        this.isLoadingMandates = true;
        this.aegisIntegrationService.getAegisMandateList(searchname, this.start, this.limit).subscribe((response: any) => {
            if (response && response.body && response.body?.success) {
                this.mandateList = response.body.data;
                this.selectedMandateId = null;
                this.currentPage = 1;
            } else {
                this.mandateList = [];
                this.selectedMandateId = null;
                this.currentPage = 1;
            }
            this.isLoadingMandates = false;
        }, (error) => {
            if(error && error.error && error.error?.message === 'ats_integration_unauthorized') {
                this.update.emit({ status: 'unauthorized', message: 'Aegis session expired. Please connect aegis.', data: null });
            }
            this.mandateList = [];
            this.selectedMandateId = null;
            this.currentPage = 1;
            this.isLoadingMandates = false;
        }
        );
    };

    get paginatedMandateList(): any[] {
        const startIndex = (this.currentPage - 1) * this.limit;
        return this.mandateList.slice(startIndex, startIndex + this.limit);
    }

    setPage(page: number): void {
        this.currentPage = page;
    }

    /**
     * Handles the selection of a mandate.
     * @param mandate The mandate to select.
     */
    selectMandate(mandate: any): void {
        this.selectedMandateId = mandate?.mandateid ?? null;
    }

    /**
     * Tags the selected candidates to the selected mandate.
     * This method uses the AegisIntegrationService to send a request to tag the candidates to the mandate.
     * It logs the response and handles success or failure accordingly.
     */
    tagCandidateToMandate(): void {
        let candidateIds: string[] = []
        if (this.calledfrom == 'talentpool') {
            candidateIds = this.candidates.map(candidate => { return Array.isArray(candidate?.basicdetails) ? candidate?.basicdetails[0]?.candidatenumber : candidate?.basicdetails?.candidatenumber}).filter(Boolean);
        } else if (this.calledfrom == 'person-search') {
            candidateIds = this.candidates.map(candidate => { return candidate?.id }).filter(Boolean);
        } else {
            candidateIds = [];
        }

        const data = {
            candidatenumber: candidateIds,
            calledFrom: this.calledfrom == 'talentpool' ? 'talentpool' : 'person-search'
        };

        this.showAddToAegisSpinner = true;
        this.aegisIntegrationService.tagCandidateToMandate(data, this.selectedMandateId!).subscribe((response: any) => {
            if (response && response.body && response.body?.success) {
                this.update.emit({ status: 'success', message: 'Candidates successfully tagged to mandate.', data: response.body?.data });
                this.toastmessage('Candidates successfully tagged to mandate.', 'success');
            } else {
                this.update.emit({ status: 'error', message: 'Failed to tag candidates to mandate.', data: null });
                this.toastmessage('Failed to tag candidates to mandate.', 'error');
            }
            this.showAddToAegisSpinner = false;

        }, (error) => {
            this.showAddToAegisSpinner = false;
            this.update.emit({ status: 'error', message: 'Failed to tag candidates to mandate.', data: error?.error?.error });
            this.toastmessage('Failed to tag candidates to mandate.', 'error');
        });
    };


    /**
     * Formats the candidates for tagging to a mandate.
     * @param candidates The list of candidates to format.
     * @returns An object containing the formatted candidates.
     */
    formatCandidatesForTagging(candidates: any[]): { candidate: any[] } {
        const toSafeString = (value: any): string => {
            if (value === null || value === undefined || value === false) {
                return '';
            }
            return String(value);
        };

        const formattedCandidates = (Array.isArray(candidates) ? candidates : []).map((candidate: any) => {
            const workexperience = (Array.isArray(candidate?.experience) ? candidate.experience : []).map((exp: any) => ({
                jobtitle: toSafeString(exp?.title?.name || exp?.title),
                company: toSafeString(exp?.company?.name),
                location_names: [toSafeString(exp?.company?.location?.name)],
                workdescription: toSafeString(exp?.summary || exp?.expsummary),
                startdate: toSafeString(exp?.start_date),
                enddate: toSafeString(exp?.end_date),
                iscurrent: exp?.end_date ? 0 : 1
            }));

            const education = (Array.isArray(candidate?.education) ? candidate.education : []).map((edu: any) => {
                const degreeList = Array.isArray(edu?.degrees) ? edu.degrees.filter(Boolean) : [];
                const majorList = Array.isArray(edu?.majors) ? edu.majors.filter(Boolean) : [];

                return {
                    schoolname: toSafeString(edu?.school?.name),
                    degree: toSafeString(
                        degreeList.length ? degreeList.join(', ') : (edu?.newField || majorList.join(', '))
                    ),
                    startdate: toSafeString(edu?.start_date),
                    enddate: toSafeString(edu?.end_date)
                };
            });

            let personalemail: string | null = null;
            let personalmobile: string | null = null;

            if (Array.isArray(candidate.existingContactDetails)) {
                candidate.existingContactDetails.forEach((contact: any) => {
                    if (contact.type === 'email' && contact.contactdetailcategory === 'personal' && !personalemail) {
                        personalemail = contact.contactdetail;
                    } else if (contact.type === 'contactnumber' && contact.contactdetailcategory === 'personal' && !personalmobile) {
                        personalmobile = contact.contactdetail;
                    }
                });
            };

            return {
                firstname: toSafeString(candidate?.first_name),
                lastname: toSafeString(candidate?.last_name ? candidate?.last_name : candidate?.full_name ? candidate?.full_name.trim().split(/\s+/).pop() : null),
                personalemail: toSafeString(personalemail),
                personalmobile: toSafeString(personalmobile),
                jobtitle: toSafeString(candidate?.job_title || candidate?.primaryjob_title),
                currentcompany: toSafeString(candidate?.job_company_name),
                industry: toSafeString(candidate?.industry || candidate?.job_company_industry),
                summary: toSafeString(candidate?.summary || candidate?.headline),
                gender: toSafeString(candidate?.gender),
                linkedin: toSafeString(candidate?.linkedin_url),
                totalexperience: toSafeString(candidate?.totaltenure ? Math.round(parseFloat(candidate.totaltenure) * 12) : ''),
                experience: workexperience,
                education,
                skills: Array.isArray(candidate?.skills) ? candidate.skills.filter((skill: any) => !!skill) : [],
                meta: candidate?.id ? candidate?.id : null,
                location_names: [toSafeString(candidate?.location_name)],
            };
        });

        return {
            candidate: formattedCandidates,
        };
    };

    /**
     * Formats the candidates from talent pool for tagging to a mandate.
     * @param candidates
     * @returns
     */
    formatTalentpoolCandidatesForTagging(candidates: any[]): { candidate: any[] } {
        const toSafeString = (value: any): string => {
            if (value === null || value === undefined || value === false) {
                return '';
            }
            return String(value);
        };

        const formattedCandidates = (Array.isArray(candidates) ? candidates : []).map((candidate: any) => {
            const basicDetail = Array.isArray(candidate?.basicdetails) && candidate.basicdetails.length > 0
                ? candidate.basicdetails[0]
                : (candidate?.basicdetails || candidate || {});

            const rootDetail = candidate || {};
            const experienceSource = Array.isArray(candidate?.experiencedetails)
                ? candidate.experiencedetails : Array.isArray(candidate?.experienceDetails)
                    ? candidate.experienceDetails
                    : Array.isArray(candidate?.experience)
                        ? candidate.experience
                        : [];
            const educationSource = Array.isArray(candidate?.educationdetails)
                ? candidate.educationdetails
                : Array.isArray(candidate?.education)
                    ? candidate.education
                    : [];
            const skillsSource = Array.isArray(candidate?.additionaldetails)
                ? candidate.additionaldetails
                : Array.isArray(candidate?.skills)
                    ? candidate.skills
                    : [];

            const workexperience = experienceSource.map((exp: any) => ({
                jobtitle: toSafeString(exp?.jobtitle || exp?.title?.name || exp?.title),
                company: toSafeString(exp?.companyname || exp?.company?.name),
                location_names: [toSafeString(exp?.locationname)],
                workdescription: toSafeString(exp?.workdescription || exp?.summary || exp?.expsummary),
                startdate: toSafeString(exp?.startdate || exp?.start_date),
                enddate: toSafeString(exp?.enddate || exp?.end_date),
                iscurrent: exp?.iscurrent ? 1 : ((exp?.enddate || exp?.end_date) ? 0 : 1)
            }));

            const education = educationSource.map((edu: any) => ({
                schoolname: toSafeString(edu?.institutename || edu?.school?.name),
                degree: toSafeString(edu?.degree || edu?.fieldofstudy || edu?.newField),
                startdate: toSafeString(edu?.startdate || edu?.start_date),
                enddate: toSafeString(edu?.enddate || edu?.end_date)
            }));

            const skills = skillsSource
                .filter((detail: any) => detail?.type === 'skills' || typeof detail === 'string')
                .map((detail: any) => typeof detail === 'string' ? detail : detail?.information)
                .filter((skill: any) => !!skill);

            const emailDetails = candidate?.contactdetails?.emails?.existingDetails;
            let personalemail = '';
            if (Array.isArray(emailDetails) && emailDetails.length > 0) {
                const personalEmailObj = emailDetails.find((email: any) => email?.contactdetailcategory === 'personal');
                const professionalEmailObj = emailDetails.find((email: any) => email?.contactdetailcategory === 'professional');
                personalemail = personalEmailObj?.contactdetail || professionalEmailObj?.contactdetail || basicDetail?.primaryemailid || rootDetail?.primaryemailid || '';
            } else {
                personalemail = basicDetail?.primaryemailid || rootDetail?.primaryemailid || '';
            }

            const totalExperienceValue = basicDetail?.totalexperience ?? rootDetail?.totalexperience ?? rootDetail?.totaltenure;
            let totalexperience = '';
            if (typeof totalExperienceValue === 'number') {
                totalexperience = toSafeString(Math.round(totalExperienceValue * 12));
            } else if (typeof totalExperienceValue === 'string' && totalExperienceValue.trim()) {
                totalexperience = toSafeString(totalExperienceValue);
            }


            return {
                firstname: toSafeString(basicDetail?.firstname ?? rootDetail?.firstname ?? basicDetail?.first_name ?? rootDetail?.first_name),
                lastname: toSafeString(basicDetail?.lastname ?? rootDetail?.lastname ?? basicDetail?.last_name ?? rootDetail?.last_name),
                personalemail: toSafeString(personalemail),
                personalmobile: toSafeString(basicDetail?.primarycontactnumber ?? rootDetail?.primarycontactnumber),
                jobtitle: toSafeString(basicDetail?.jobtitle ?? rootDetail?.jobtitle ?? rootDetail?.job_title),
                currentcompany: toSafeString(basicDetail?.companyname ?? rootDetail?.companyname ?? rootDetail?.job_company_name),
                industry: toSafeString(basicDetail?.industryname ?? rootDetail?.industryname ?? rootDetail?.job_company_industry),
                summary: toSafeString(basicDetail?.summary ?? rootDetail?.summary ?? rootDetail?.headline),
                gender: toSafeString(basicDetail?.gender ?? rootDetail?.gender),
                linkedin: toSafeString(basicDetail?.profileurl ?? rootDetail?.profileurl ?? rootDetail?.linkedin_url),
                totalexperience: totalexperience,
                experience: workexperience,
                education,
                skills,
                meta: toSafeString(basicDetail?.candidatenumber),
                location_names: [toSafeString(basicDetail?.locationname ?? rootDetail?.locationname)]
            };
        });

        return {
            candidate: formattedCandidates
        };
    };

    /**
     * Dismisses the modal and emits a cancel event.
     */
    dismissModel(): void {
        this.update.emit({ status: 'cancel', message: '' });
    }
};
